import { useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';

import type {
  PublicSignerFieldResponse,
  PublicSignerSignatureValue
} from '../publicSignerTypes';
import { EsignFieldKind } from '../publicSignerTypes';

import { PublicSignerSignatureFieldInput } from './PublicSignerSignatureFieldInput';

const MAX_TYPED_INITIALS_LENGTH = 20;

interface DialogCopy {
  applyLabel: string;
  drawHelperText: string;
  subtitle: string;
  title: string;
  typedHelperText: string;
  typedLabel: string;
  typedMaxLength?: number;
}

function getDialogCopy(field: PublicSignerFieldResponse | null): DialogCopy {
  if (field?.field_kind === EsignFieldKind.Initial) {
    return {
      applyLabel: 'Apply initials',
      drawHelperText: 'Draw your initials in the box',
      subtitle:
        'Choose to type or draw your initials and apply them to the document',
      title: 'Initials',
      typedHelperText: 'Type your initials',
      typedLabel: 'Typed initials',
      typedMaxLength: MAX_TYPED_INITIALS_LENGTH
    };
  }

  return {
    applyLabel: 'Apply signature',
    drawHelperText: 'Draw your signature in the box.',
    subtitle: 'Choose a typed or drawn signature and apply it to the document.',
    title: field?.label ?? 'Signature',
    typedHelperText: 'Type your full legal name.',
    typedLabel: 'Typed signature'
  };
}

export function PublicSignerSignatureDialog({
  error,
  field,
  initialValue,
  onClose,
  onSave,
  presetDrawnImageDataUrl,
  presetMode,
  presetTypedName
}: {
  error?: string;
  field: PublicSignerFieldResponse | null;
  initialValue: PublicSignerSignatureValue | null;
  onClose: () => void;
  onSave: (value: PublicSignerSignatureValue | null) => void;
  presetDrawnImageDataUrl: string | null;
  presetMode: 'typed' | 'drawn' | null;
  presetTypedName: string | null;
}) {
  const [draftValue, setDraftValue] =
    useState<PublicSignerSignatureValue | null>(initialValue);
  const lastFieldIdRef = useRef<string | null>(field?.id ?? null);
  const copy = getDialogCopy(field);

  // Sync reset on field change: useEffect would run after FieldInput
  // already mounted with stale draftValue, leaking substate into the new field.
  const currentFieldId = field?.id ?? null;
  if (lastFieldIdRef.current !== currentFieldId) {
    lastFieldIdRef.current = currentFieldId;
    setDraftValue(initialValue);
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={Boolean(field)}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxWidth: '480px'
        }
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 2, pb: 1 }}>{copy.title}</DialogTitle>
      <DialogContent sx={{ px: 3, pb: 0 }}>
        <Stack spacing={2} mt={0.5}>
          <Typography color="text.secondary" variant="body2">
            {copy.subtitle}
          </Typography>
          <PublicSignerSignatureFieldInput
            key={field?.id}
            drawHelperText={copy.drawHelperText}
            error={error}
            onChange={setDraftValue}
            presetDrawnImageDataUrl={presetDrawnImageDataUrl}
            presetMode={presetMode}
            presetTypedName={presetTypedName}
            typedHelperText={copy.typedHelperText}
            typedLabel={copy.typedLabel}
            typedMaxLength={copy.typedMaxLength}
            value={draftValue}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={Boolean(field?.is_required) && !draftValue}
          onClick={() => {
            onSave(draftValue);
          }}
          variant="contained"
        >
          {copy.applyLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
